import { Awareness } from "y-protocols/awareness";
import { parseCanvasPresenterViewport } from "@playbookai/types";
import { decodeSecureControlStatelessV1 } from "./contracts.js";
import { SecureClientBindingStoreV1 } from "./binding-store.js";
import { assertTrustedSessionLabelV1, SecurePresenceProjectionProjectorV1, } from "./presence-projection.js";
export function createSecureTrustedPresenceControllerV1(options) {
    return new SecureTrustedPresenceController(options);
}
class SecureTrustedPresenceController {
    awareness;
    localClientId;
    mode;
    store = new SecureClientBindingStoreV1();
    projector = new SecurePresenceProjectionProjectorV1();
    listeners = new Set();
    localDomain = null;
    queuedLocalPresence;
    destroyed = false;
    presenceVersion = 1;
    latestLocalPresence = null;
    constructor(options) {
        this.mode = options.mode;
        if (options.mode === "edit" || options.mode === "participate") {
            requireUint32(options.document.clientID);
            this.awareness = new Awareness(options.document);
            this.awareness.setLocalState(null);
            this.localClientId = options.document.clientID;
        }
        else {
            this.awareness = null;
            this.localClientId = null;
        }
    }
    handleServerStateless(payload) {
        const decoded = decodeSecureControlStatelessV1(payload);
        if (decoded.kind === "not_control")
            return Object.freeze(decoded);
        if (decoded.kind === "terminal")
            return terminalIngress();
        let control;
        try {
            control = canonicalizeControl(decoded.control);
        }
        catch {
            return terminalIngress();
        }
        if (this.destroyed)
            return handledIngress();
        const result = this.store.apply(control);
        if (result.kind === "terminal")
            return terminalIngress();
        if (result.kind === "applied") {
            this.captureLocalDomain(control);
            this.refreshProjection();
        }
        return handledIngress();
    }
    getPresence() {
        return this.projector.snapshot();
    }
    subscribe(listener) {
        if (this.destroyed)
            return () => undefined;
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    setPresence(input) {
        this.assertPublisherCapable();
        this.latestLocalPresence = input;
        this.queuedLocalPresence = canonicalizeLocalPresence(input, this.presenceVersion === 2);
        this.flushQueuedLocalPresence();
    }
    enablePresenceV2() {
        if (this.destroyed ||
            this.presenceVersion === 2 ||
            this.localDomain?.document.docType !== "vsm_canvas") {
            return;
        }
        this.presenceVersion = 2;
        if (this.latestLocalPresence !== null) {
            this.queuedLocalPresence = canonicalizeLocalPresence(this.latestLocalPresence, true);
            this.flushQueuedLocalPresence();
        }
    }
    clearPresence() {
        if (this.destroyed || this.awareness === null)
            return;
        this.latestLocalPresence = null;
        this.queuedLocalPresence = null;
        this.flushQueuedLocalPresence();
    }
    destroy() {
        if (this.destroyed)
            return;
        this.destroyed = true;
        if (this.awareness) {
            this.awareness.setLocalState(null);
            this.awareness.destroy();
        }
        this.queuedLocalPresence = undefined;
        this.store.clear();
        const previous = this.projector.snapshot();
        const empty = this.projector.clear();
        if (empty !== previous)
            this.notify(empty);
        this.listeners.clear();
    }
    assertPublisherCapable() {
        if (this.destroyed ||
            (this.mode !== "edit" && this.mode !== "participate") ||
            this.awareness === null ||
            this.localClientId === null) {
            throw new Error("secure_presence_not_writable");
        }
    }
    captureLocalDomain(control) {
        if (this.localClientId === null)
            return;
        if ((control.type === "binding_snapshot" ||
            control.type === "binding_upsert") &&
            control.binding.state === "bound" &&
            control.binding.clientId === this.localClientId &&
            this.store.binding(control.domain, this.localClientId).state === "bound") {
            if (this.localDomain === null)
                this.localDomain = control.domain;
            if (!sameDomain(this.localDomain, control.domain))
                return;
            this.flushQueuedLocalPresence();
        }
    }
    flushQueuedLocalPresence() {
        if (this.queuedLocalPresence === undefined ||
            this.awareness === null ||
            this.localClientId === null ||
            this.localDomain === null ||
            this.store.binding(this.localDomain, this.localClientId).state !== "bound") {
            return;
        }
        const next = this.queuedLocalPresence;
        this.queuedLocalPresence = undefined;
        this.awareness.setLocalState(next);
    }
    refreshProjection() {
        const previous = this.projector.snapshot();
        const next = this.projector.project(this.store.snapshot());
        if (next !== previous)
            this.notify(next);
    }
    notify(projection) {
        for (const listener of [...this.listeners])
            listener(projection);
    }
}
function sameDomain(left, right) {
    return (left.originId === right.originId &&
        left.documentGeneration === right.documentGeneration &&
        left.document.tenantId === right.document.tenantId &&
        left.document.docType === right.document.docType &&
        left.document.entityId === right.document.entityId);
}
function canonicalizeControl(control) {
    if (control.type === "presence_upsert") {
        return Object.freeze({
            ...control,
            entry: canonicalizePresenceEntry(control.entry, control.version === 2),
        });
    }
    if (control.type === "presence_snapshot") {
        const entries = control.entries.map((entry) => canonicalizePresenceEntry(entry, control.version === 2));
        return Object.freeze({ ...control, entries: Object.freeze(entries) });
    }
    return control;
}
function canonicalizePresenceEntry(entry, allowPresenterViewport = false) {
    const value = canonicalizeTrustedPresence(entry.value, entry.clientId, allowPresenterViewport);
    return Object.freeze({ ...entry, value });
}
function canonicalizeTrustedPresence(input, clientId, allowPresenterViewport = false) {
    if (input === null)
        return null;
    const value = plainRecord(input);
    exactKeys(value, [
        "cursor",
        "selection",
        "activeWidget",
        "online",
        "sessionLabel",
        ...(allowPresenterViewport ? ["presenterViewport"] : []),
    ]);
    if (value["online"] !== true)
        throw new Error("online");
    const sessionLabel = nonemptyString(value["sessionLabel"]);
    assertTrustedSessionLabelV1(sessionLabel, clientId);
    const cursor = Object.prototype.hasOwnProperty.call(value, "cursor")
        ? cursorValue(value["cursor"])
        : undefined;
    const selection = Object.prototype.hasOwnProperty.call(value, "selection")
        ? boundedStringOrNull(value["selection"])
        : undefined;
    const activeWidget = Object.prototype.hasOwnProperty.call(value, "activeWidget")
        ? activeWidgetValue(value["activeWidget"])
        : undefined;
    const presenterViewport = allowPresenterViewport &&
        Object.prototype.hasOwnProperty.call(value, "presenterViewport")
        ? value["presenterViewport"] === null
            ? null
            : parseCanvasPresenterViewport(value["presenterViewport"])
        : undefined;
    return Object.freeze({
        ...(cursor === undefined ? {} : { cursor }),
        ...(selection === undefined ? {} : { selection }),
        ...(activeWidget === undefined ? {} : { activeWidget }),
        ...(presenterViewport === undefined ? {} : { presenterViewport }),
        online: true,
        sessionLabel,
    });
}
function canonicalizeLocalPresence(input, allowPresenterViewport = false) {
    const value = plainRecord(input);
    exactKeys(value, [
        "cursor",
        "selection",
        "activeWidget",
        "online",
        "presenterViewport",
    ]);
    const cursor = Object.prototype.hasOwnProperty.call(value, "cursor")
        ? cursorValue(value["cursor"])
        : undefined;
    const selection = Object.prototype.hasOwnProperty.call(value, "selection")
        ? boundedStringOrNull(value["selection"])
        : undefined;
    const activeWidget = Object.prototype.hasOwnProperty.call(value, "activeWidget")
        ? activeWidgetValue(value["activeWidget"])
        : undefined;
    const presenterViewport = allowPresenterViewport &&
        Object.prototype.hasOwnProperty.call(value, "presenterViewport")
        ? value["presenterViewport"] === null
            ? null
            : parseCanvasPresenterViewport(value["presenterViewport"])
        : undefined;
    const online = Object.prototype.hasOwnProperty.call(value, "online")
        ? booleanValue(value["online"])
        : true;
    if (!online)
        return null;
    return Object.freeze({
        ...(cursor === undefined ? {} : { cursor }),
        ...(selection === undefined ? {} : { selection }),
        ...(activeWidget === undefined ? {} : { activeWidget }),
        ...(presenterViewport === undefined ? {} : { presenterViewport }),
        online: true,
    });
}
function cursorValue(input) {
    if (input === null)
        return null;
    const value = plainRecord(input);
    exactKeys(value, ["x", "y"]);
    return Object.freeze({
        x: finiteNumber(value["x"]),
        y: finiteNumber(value["y"]),
    });
}
function activeWidgetValue(input) {
    if (input === null)
        return null;
    const value = plainRecord(input);
    exactKeys(value, ["id", "mode"]);
    const id = boundedString(value["id"]);
    const mode = value["mode"];
    if (mode !== "selected" && mode !== "editing" && mode !== "dragging") {
        throw new Error("activeWidget.mode");
    }
    return Object.freeze({ id, mode });
}
function boundedStringOrNull(input) {
    return input === null ? null : boundedString(input);
}
function boundedString(input) {
    const value = nonemptyString(input);
    if (value.length > 128)
        throw new Error("bounded string");
    return value;
}
function nonemptyString(input) {
    if (typeof input !== "string" || input.length === 0) {
        throw new Error("string");
    }
    return input;
}
function finiteNumber(input) {
    if (typeof input !== "number" || !Number.isFinite(input)) {
        throw new Error("finite number");
    }
    return input;
}
function booleanValue(input) {
    if (typeof input !== "boolean")
        throw new Error("boolean");
    return input;
}
function plainRecord(input) {
    if (typeof input !== "object" ||
        input === null ||
        Array.isArray(input) ||
        Object.getPrototypeOf(input) !== Object.prototype) {
        throw new Error("plain object");
    }
    return input;
}
function exactKeys(input, allowed) {
    const allow = new Set(allowed);
    if (Object.keys(input).some((key) => !allow.has(key))) {
        throw new Error("unknown field");
    }
}
function requireUint32(input) {
    if (!Number.isInteger(input) || input < 0 || input > 0xffff_ffff) {
        throw new Error("invalid client id");
    }
}
function handledIngress() {
    return Object.freeze({ kind: "handled" });
}
function terminalIngress() {
    return Object.freeze({
        kind: "terminal",
        reason: "binding_protocol_violation",
    });
}
//# sourceMappingURL=trusted-presence.js.map